// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Drives automatic shot marking from a round's lifecycle, and mirrors the fused
// shots the server broadcasts back. Android only for capture — the marks ride
// the same durable offline queue as scores, so a dead spot on the course
// delays them rather than losing them.

import { useEffect, useRef, useState } from 'react';

import { getBatteryFraction } from '@/services/deviceSignals';
import { sendOrQueue, type QueueSender } from '@/services/offlineQueue';
import {
  isSupported,
  setShotTrackingHole,
  startShotTracking,
  stopShotTracking,
} from '@/services/shotTracking';
import { useShotTrackingEnabled } from '@/stores/shotTrackingState';

import { emitShotTrackingDebug } from './debugBus';

export type UseShotTrackingParams = {
  roundId: string | null;
  /** The logged-in user's player id in this round; capture is only theirs. */
  playerId: string | null;
  holeNumber: number | null;
  /** Capture runs only while the round is in play. */
  isRoundActive: boolean;
  /** The hole screen's socket, for send-or-queue. */
  getSender: () => QueueSender | null;
  subjectKey: string;
};

export type ShotTrackingStatus = {
  /** True while this round's capture session is actually running. */
  trackingActive: boolean;
};

export const useShotTracking = ({
  roundId,
  playerId,
  holeNumber,
  isRoundActive,
  getSender,
  subjectKey,
}: UseShotTrackingParams): ShotTrackingStatus => {
  const enabled = useShotTrackingEnabled();
  const [trackingActive, setTrackingActive] = useState(false);

  // Refs so the capture session survives re-renders and socket churn without
  // restarting, mirroring useCourseTrace's token handling.
  const getSenderRef = useRef(getSender);
  getSenderRef.current = getSender;
  const subjectKeyRef = useRef(subjectKey);
  subjectKeyRef.current = subjectKey;
  const playerIdRef = useRef(playerId);
  playerIdRef.current = playerId;

  useEffect(() => {
    if (!isSupported() || !enabled || !roundId || !playerId || !isRoundActive) {
      return undefined;
    }

    let cancelled = false;

    const begin = async () => {
      const batteryFraction = await getBatteryFraction();
      if (cancelled) return;

      const result = await startShotTracking({
        roundId,
        playerId,
        hasConsent: true,
        batteryFraction,
        onMark: (mark) => {
          const ownerPlayerId = playerIdRef.current;
          if (!ownerPlayerId || mark.holeNumber === null) {
            emitShotTrackingDebug({
              kind: 'send',
              at: Date.now(),
              markId: mark.markId,
              outcome: 'skipped',
            });
            return;
          }
          void sendOrQueue(
            getSenderRef.current(),
            { subjectKey: subjectKeyRef.current, roundId },
            'shot.mark',
            mark.markId,
            {
              type: 'shot.mark',
              roundId,
              playerId: ownerPlayerId,
              holeNumber: mark.holeNumber,
              markId: mark.markId,
              source: 'phone-audio',
              kind: mark.kind,
              lat: mark.lat,
              lon: mark.lon,
              accuracyMeters: mark.accuracyMeters,
              timestamp: mark.timestamp,
            }
          ).then((outcome) => {
            emitShotTrackingDebug({ kind: 'send', at: Date.now(), markId: mark.markId, outcome });
          });
        },
      });
      // Honest status: permission refusals and platform gates read as "not
      // tracking", so the UI never claims a session that is not running.
      if (!cancelled) setTrackingActive(result === 'started');
    };

    void begin();

    return () => {
      cancelled = true;
      setTrackingActive(false);
      void stopShotTracking();
    };
    // playerId is deliberately in the deps: the round resolving "who am I"
    // late must start capture then, not never.
  }, [enabled, isRoundActive, playerId, roundId]);

  useEffect(() => {
    if (!isSupported()) return;
    setShotTrackingHole(holeNumber);
  }, [holeNumber]);

  return { trackingActive };
};

export type RoundShotView = {
  shotNumber: number;
  distanceMeters: number | null;
  confidence: string;
  occurredAt: string | null;
};

type RoundShotsFrame = {
  type?: unknown;
  roundId?: unknown;
  playerId?: unknown;
  hole?: unknown;
  shots?: unknown;
  putts?: unknown;
};

/**
 * The fused shots the server has for one player on one hole, kept current from
 * round.shots frames on the given socket. Empty until the first broadcast —
 * shots are visual sugar, so there is deliberately no fetch-on-mount.
 */
export type RoundHoleShots = {
  shots: RoundShotView[];
  /** Detected putts on the hole — a count, kept apart from measured shots. */
  putts: number;
};

export const useRoundShots = (
  client: { onMessage: (handler: (message: unknown) => void) => () => void } | null,
  roundId: string | null,
  playerId: string | null,
  holeNumber: number | null
): RoundHoleShots => {
  const [shotsByKey, setShotsByKey] = useState<Record<string, RoundHoleShots>>({});

  useEffect(() => {
    if (!client || !roundId) return undefined;
    return client.onMessage((message) => {
      const frame = message as RoundShotsFrame;
      if (!frame) return;
      // Wrist peaks (putt.probe) are deliberately NOT read here: the BFF
      // relays them user-scoped and round-subscribed sockets never receive
      // them. The debug page listens on the shared socket instead, which is
      // what lets a putt test run with no round open on the phone.
      if (frame.type !== 'round.shots') return;
      if (frame.roundId !== roundId) return;
      if (typeof frame.playerId !== 'string' || typeof frame.hole !== 'number') return;
      const shots = Array.isArray(frame.shots) ? frame.shots : [];
      const mapped: RoundShotView[] = shots
        .map((shot) => {
          if (!shot || typeof shot !== 'object') return null;
          const record = shot as Record<string, unknown>;
          if (typeof record.shotNumber !== 'number') return null;
          return {
            shotNumber: record.shotNumber,
            distanceMeters: typeof record.distanceMeters === 'number' ? record.distanceMeters : null,
            confidence: typeof record.confidence === 'string' ? record.confidence : 'low',
            occurredAt: typeof record.occurredAt === 'string' ? record.occurredAt : null,
          };
        })
        .filter((shot): shot is RoundShotView => shot !== null);
      const putts = typeof frame.putts === 'number' ? frame.putts : 0;
      // The debug page shows the server's answer next to what the device sent,
      // but only the wearer's own shots — that is all this hook tracks.
      if (frame.playerId === playerId) {
        emitShotTrackingDebug({
          kind: 'server',
          at: Date.now(),
          hole: frame.hole,
          putts,
          shots: mapped.map((shot) => ({
            shotNumber: shot.shotNumber,
            distanceMeters: shot.distanceMeters,
            confidence: shot.confidence,
          })),
        });
      }
      setShotsByKey((current) => ({
        ...current,
        [`${frame.playerId}:${frame.hole}`]: { shots: mapped, putts },
      }));
    });
  }, [client, playerId, roundId]);

  // Reset when the round changes so a re-used screen never shows stale shots.
  useEffect(() => {
    setShotsByKey({});
  }, [roundId]);

  if (!playerId || holeNumber === null) return EMPTY_HOLE_SHOTS;
  return shotsByKey[`${playerId}:${holeNumber}`] ?? EMPTY_HOLE_SHOTS;
};

const EMPTY_HOLE_SHOTS: RoundHoleShots = { shots: [], putts: 0 };
